import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SPONSOR_PITCH_TABS } from '../lib/sponsorPitchRegistry'

type ReadinessState = 'approved' | 'pending' | 'blocked' | 'skipped'

interface MemberRecord {
  id: string
  email: string
  role: string
  status: string
  allowed_sponsors?: string[]
  rights?: {
    bundleKey?: string
    missingTableFallback?: boolean
  }
}

interface AccessBundle {
  key: string
  label: string
}

interface PreapprovalRule {
  id?: string
  email_domain?: string
  role?: string
  status?: string
  profile_type?: string
  bundle_key?: string
  access_bundle_key?: string
  allowed_sponsors?: string[]
  active?: boolean
}

interface MembersResponse {
  members?: MemberRecord[]
  bundles?: AccessBundle[]
  preapprovalRules?: PreapprovalRule[]
  error?: string
}

interface SponsorVersion {
  id: string
  version: number
  state: 'draft' | 'published' | 'archived'
  created_at?: string
  updated_at?: string
  published_at?: string | null
  approved_at?: string | null
}

interface SponsorCmsResponse {
  page?: {
    slug: string
    status?: string | null
    updated_at?: string | null
    published_version_id?: string | null
  } | null
  draft?: SponsorVersion | null
  published?: SponsorVersion | null
  fallback?: unknown
  source?: string
  warning?: string
  error?: string
}

interface DeliveryRecord {
  id: string
  channel: string
  status: 'pending' | 'sent' | 'failed' | 'blocked' | 'skipped'
  event_id?: string
  provider_ref?: string | null
  sent_at?: string | null
  created_at?: string
  last_error?: string | null
}

interface DeliveriesResponse {
  deliveries?: DeliveryRecord[]
  warning?: string
  error?: string
}

interface RouteProbe {
  ok: boolean
  status: number
  error?: string
  source?: string
}

interface SponsorReadiness {
  slug: string
  label: string
  description: string
  cms?: SponsorCmsResponse
  cmsError?: string
  protectedRoute?: RouteProbe
  tempRoute?: RouteProbe
}

const DELIVERY_STATUSES = ['pending', 'failed', 'blocked', 'sent'] as const
const TEMP_COPY_DATE = '2026-06-07'
const SPECIAL_TEMP_COPY_DATES: Record<string, string> = {
  'the-licenced': '2026-05-27',
}
const EXPECTED_DOMAIN_RULES: Record<string, string[]> = {
  'the-licenced': ['thelicenced.com', 'media.thelicenced.com'],
}

function statusClass(state: ReadinessState) {
  return `admin-status admin-status-${state}`
}

function formatDate(value?: string | null) {
  if (!value) return 'No timestamp'
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function sponsorTempPath(slug: string, kind: 'sponsor' | 'value-report' = 'sponsor') {
  const date = SPECIAL_TEMP_COPY_DATES[slug] ?? TEMP_COPY_DATE
  return `/temporary/${slug}-${kind}-copy-${date}.html`
}

function normalizeDomain(value?: string) {
  return (value ?? '').trim().toLowerCase().replace(/^@+/, '').replace(/\.+$/, '')
}

function inferredRuleBundleKey(rule: PreapprovalRule) {
  const storedBundleKey = rule.access_bundle_key ?? rule.bundle_key
  if (storedBundleKey) return storedBundleKey
  if (rule.status === 'blocked') return 'blocked'
  if (rule.status === 'pending') return 'guest_pending'
  if (rule.role === 'sponsor' || rule.profile_type === 'sponsor') return 'sponsor_viewer'
  return undefined
}

function ruleSupportsSponsor(rule: PreapprovalRule, sponsorSlug: string) {
  const bundleKey = inferredRuleBundleKey(rule)
  return rule.active !== false
    && rule.status === 'approved'
    && rule.role === 'sponsor'
    && rule.profile_type === 'sponsor'
    && bundleKey === 'sponsor_viewer'
    && (rule.allowed_sponsors ?? []).includes(sponsorSlug)
}

async function fetchJson<T>(path: string): Promise<{ data?: T; status: number; error?: string }> {
  try {
    const res = await fetch(path, { credentials: 'include' })
    const contentType = res.headers.get('content-type') ?? ''
    const data = contentType.includes('json') ? await res.json().catch(() => ({})) as T & { error?: string } : undefined
    if (!res.ok) return { status: res.status, data, error: data?.error ?? `${res.status}` }
    return { status: res.status, data }
  } catch (error) {
    return { status: 0, error: error instanceof Error ? error.message : 'request_failed' }
  }
}

function routeState(probe?: RouteProbe): ReadinessState {
  if (!probe) return 'pending'
  if (probe.ok) return 'approved'
  if (probe.error === 'nda_required') return 'pending'
  if (probe.status === 404) return 'blocked'
  if (probe.status === 0) return 'pending'
  return 'blocked'
}

function cmsState(item?: SponsorReadiness): ReadinessState {
  if (!item) return 'pending'
  if (item.cmsError) return item.cmsError.includes('migration') || item.cmsError.includes('database') ? 'blocked' : 'pending'
  if (item.cms?.published) return 'approved'
  if (item.cms?.fallback) return 'pending'
  return 'blocked'
}

function latestSponsorTimestamp(item: SponsorReadiness) {
  return item.cms?.published?.published_at
    ?? item.cms?.draft?.created_at
    ?? item.cms?.page?.updated_at
    ?? null
}

function compareDates(a?: string | null, b?: string | null) {
  if (!a || !b) return false
  return new Date(a).getTime() > new Date(b).getTime()
}

export default function AdminDashboardPage() {
  const { isLoggedIn, loading, status, rights, email, role } = useAuth()
  const [membersData, setMembersData] = useState<MembersResponse | null>(null)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([])
  const [deliveriesWarning, setDeliveriesWarning] = useState<string | null>(null)
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null)
  const [sponsors, setSponsors] = useState<SponsorReadiness[]>([])
  const [sponsorsLoading, setSponsorsLoading] = useState(false)
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null)

  const canMembers = rights['admin.members.manage'] === true
  const canContent = rights['admin.content.manage'] === true
  const canIntegrations = rights['integrations.delivery.manage'] === true
  const canKnockout = rights['admin.knockout.manage'] === true
  const canViewAll = rights['pitch.view_all'] === true
  const canAdmin = isLoggedIn && status === 'approved' && (canMembers || canContent || canIntegrations || canKnockout || canViewAll)

  async function loadDashboard() {
    setLastCheckedAt(new Date().toISOString())
    if (canMembers) {
      setMembersError(null)
      const result = await fetchJson<MembersResponse>('/api/admin/members')
      if (result.error) {
        setMembersError(result.error)
        setMembersData(null)
      } else {
        setMembersData(result.data ?? {})
      }
    }

    if (canIntegrations) {
      setDeliveriesError(null)
      setDeliveriesWarning(null)
      const results = await Promise.all(DELIVERY_STATUSES.map(deliveryStatus =>
        fetchJson<DeliveriesResponse>(`/api/admin/integrations/deliveries?status=${deliveryStatus}&limit=20&channel=slack`),
      ))
      const nextDeliveries: DeliveryRecord[] = []
      for (const result of results) {
        if (result.error) {
          setDeliveriesError(result.error)
        }
        if (result.data?.warning) setDeliveriesWarning(result.data.warning)
        nextDeliveries.push(...(result.data?.deliveries ?? []))
      }
      setDeliveries(nextDeliveries)
    }

    setSponsorsLoading(true)
    const nextSponsors = await Promise.all(SPONSOR_PITCH_TABS.map(async sponsor => {
      const item: SponsorReadiness = { ...sponsor }
      if (canContent) {
        const cms = await fetchJson<SponsorCmsResponse>(`/api/admin/sponsor-pages/${encodeURIComponent(sponsor.slug)}`)
        if (cms.error) item.cmsError = cms.error
        item.cms = cms.data
      }
      if (canViewAll || canContent) {
        const protectedProbe = await fetchJson<{ error?: string; source?: string }>(`/api/sponsors/${encodeURIComponent(sponsor.slug)}`)
        item.protectedRoute = {
          ok: !protectedProbe.error,
          status: protectedProbe.status,
          error: protectedProbe.error,
          source: protectedProbe.data?.source,
        }
      }
      if (sponsor.slug !== 'master') {
        const tempProbe = await fetchJson<{ error?: string; source?: string }>(`/api/temporary/sponsors/${encodeURIComponent(sponsor.slug)}`)
        item.tempRoute = {
          ok: !tempProbe.error,
          status: tempProbe.status,
          error: tempProbe.error,
          source: tempProbe.data?.source,
        }
      }
      return item
    }))
    setSponsors(nextSponsors)
    setSponsorsLoading(false)
  }

  useEffect(() => {
    if (!loading && canAdmin) void loadDashboard()
  }, [loading, canAdmin])

  const members = membersData?.members ?? []
  const preapprovalRules = membersData?.preapprovalRules ?? []
  const pendingMembers = members.filter(member => member.status === 'pending')
  const approvedMembers = members.filter(member => member.status === 'approved')
  const sponsorMembers = members.filter(member => (member.allowed_sponsors ?? []).length > 0)
  const missingAccessTable = members.some(member => member.rights?.missingTableFallback)
  const deliveryCounts = {
    pending: deliveries.filter(delivery => delivery.status === 'pending').length,
    failed: deliveries.filter(delivery => delivery.status === 'failed').length,
    blocked: deliveries.filter(delivery => delivery.status === 'blocked').length,
    sent: deliveries.filter(delivery => delivery.status === 'sent').length,
  }
  const sentSlackEvidence = deliveries.filter(delivery =>
    delivery.status === 'sent'
    && delivery.channel === 'slack'
    && Boolean(delivery.provider_ref || delivery.sent_at),
  ).length
  const expectedRuleTotal = Object.values(EXPECTED_DOMAIN_RULES).reduce((sum, domains) => sum + domains.length, 0)
  const readyExpectedRuleCount = Object.entries(EXPECTED_DOMAIN_RULES).reduce((sum, [slug, domains]) => {
    return sum + domains.filter(domain => preapprovalRules.some(rule => normalizeDomain(rule.email_domain) === domain && ruleSupportsSponsor(rule, slug))).length
  }, 0)

  const blockers = useMemo(() => {
    const next: Array<{ title: string; body: string; href?: string }> = []
    if (membersError) next.push({ title: 'Members API', body: membersError, href: '/admin/members' })
    if (membersData && preapprovalRules.length === 0) next.push({ title: 'Preapproval persistence', body: 'No persisted domain rules visible. Built-in fallback is not production evidence.', href: '/admin/members' })
    if (missingAccessTable) next.push({ title: 'Access rights table', body: 'Member rights are using fallback defaults. Apply access migrations before sponsor access QA.', href: '/admin/members' })
    if (deliveriesError) next.push({ title: 'Slack delivery API', body: deliveriesError, href: '/admin/integrations' })
    if (deliveriesWarning) next.push({ title: 'Notification migration', body: deliveriesWarning, href: '/admin/integrations' })
    if (deliveryCounts.failed || deliveryCounts.blocked) next.push({ title: 'Slack delivery failures', body: `${deliveryCounts.failed} failed and ${deliveryCounts.blocked} blocked Slack rows need review.`, href: '/admin/integrations' })
    for (const item of sponsors) {
      const expectedDomains = EXPECTED_DOMAIN_RULES[item.slug] ?? []
      const matchingRules = preapprovalRules.filter(rule => expectedDomains.includes(normalizeDomain(rule.email_domain)) && ruleSupportsSponsor(rule, item.slug))
      if (item.cmsError) next.push({ title: `${item.label} CMS`, body: item.cmsError, href: `/admin/sponsor-pages/${item.slug}` })
      if (!item.cms?.published && canContent) next.push({ title: `${item.label} published page`, body: 'No published sponsor CMS version visible.', href: `/admin/sponsor-pages/${item.slug}` })
      if (expectedDomains.length && matchingRules.length < expectedDomains.length) {
        next.push({ title: `${item.label} preapproval rules`, body: `${matchingRules.length}/${expectedDomains.length} expected domain rules are persisted and usable.`, href: canMembers ? '/admin/members' : undefined })
      }
      if (item.protectedRoute?.error === 'sponsor_access_required') next.push({ title: `${item.label} protected route`, body: 'Current operator session cannot open this protected sponsor route.', href: `/sponsors/${item.slug}` })
      if (item.tempRoute && !item.tempRoute.ok) next.push({ title: `${item.label} temporary copy`, body: item.tempRoute.error ?? `HTTP ${item.tempRoute.status}`, href: sponsorTempPath(item.slug) })
    }
    return next
  }, [membersError, membersData, preapprovalRules, missingAccessTable, deliveriesError, deliveriesWarning, deliveryCounts.failed, deliveryCounts.blocked, sponsors, canContent, canMembers])

  if (loading) {
    return <div className="admin-page"><p className="admin-muted">Checking command center access...</p></div>
  }

  if (!isLoggedIn) return <Navigate to="/signin?next=/admin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This command center is only available to approved operators with an admin, delivery, content, or pitch-wide right.</p>
        <Link to="/" className="btn btn-secondary">Back to public home</Link>
      </div>
    )
  }

  const primaryAdminLink = canMembers
    ? { to: '/admin/members', label: 'Members CMS' }
    : canContent
      ? { to: '/admin/sponsor-pages/master', label: 'Sponsor CMS' }
      : canIntegrations
        ? { to: '/admin/integrations', label: 'Deliveries' }
        : canKnockout
          ? { to: '/admin/knockout', label: 'Knockout' }
          : { to: '/status', label: 'Status' }
  const migrationState: ReadinessState = !canMembers ? 'skipped' : membersError || deliveriesWarning || missingAccessTable || readyExpectedRuleCount < expectedRuleTotal ? 'pending' : 'approved'
  const slackState: ReadinessState = deliveriesError || deliveryCounts.failed || deliveryCounts.blocked ? 'blocked' : deliveryCounts.pending || sentSlackEvidence === 0 ? 'pending' : 'approved'
  const sponsorContentState: ReadinessState = sponsorsLoading || sponsors.length === 0 ? 'pending' : sponsors.some(item => cmsState(item) === 'blocked') ? 'blocked' : sponsors.some(item => cmsState(item) === 'pending') ? 'pending' : 'approved'

  return (
    <div className="admin-page admin-dashboard-page">
      <header className="admin-header">
        <div>
          <span className="legal-eyebrow">Admin · command center</span>
          <h1 className="admin-heading">Ops Command Center</h1>
          <p className="admin-muted">
            One readiness surface for auth, member access, sponsor content, Slack delivery, migration state, and route smoke.
          </p>
          <div className="admin-status-strip">
            <span><strong>Operator</strong>{email ?? 'unknown'}</span>
            <span><strong>Role</strong>{role ?? 'unknown'}</span>
            <span><strong>Checked</strong>{formatDate(lastCheckedAt)}</span>
          </div>
        </div>
        <div className="admin-member-actions">
          <button className="btn btn-secondary" type="button" onClick={() => void loadDashboard()}>Refresh</button>
          <Link to={primaryAdminLink.to} className="btn btn-primary">{primaryAdminLink.label}</Link>
        </div>
      </header>

      <section className="admin-dashboard-gates" aria-label="Operational gates">
        <article className="admin-card admin-dashboard-gate">
          <span className={statusClass('approved')}>approved</span>
          <h2>Auth</h2>
          <p>Current operator session is approved. Sponsor-user proof still requires live OTP evidence.</p>
          <Link to="/account">Open account</Link>
        </article>
        <article className="admin-card admin-dashboard-gate">
          <span className={statusClass(membersError ? 'blocked' : canMembers ? 'approved' : 'skipped')}>{canMembers ? (membersError ? 'blocked' : 'visible') : 'no access'}</span>
          <h2>Members / access</h2>
          <p>{canMembers ? `${approvedMembers.length} approved, ${pendingMembers.length} pending, ${sponsorMembers.length} with sponsor grants.` : 'This operator lacks member-management rights.'}</p>
          {canMembers ? <Link to="/admin/members">Review members</Link> : <span className="admin-dashboard-note">Members CMS hidden for this operator.</span>}
        </article>
        <article className="admin-card admin-dashboard-gate">
          <span className={statusClass(canContent ? sponsorContentState : 'skipped')}>{canContent ? sponsorContentState : 'no access'}</span>
          <h2>Sponsor content</h2>
          <p>{sponsorsLoading ? 'Checking sponsor CMS...' : `${sponsors.filter(item => item.cms?.published).length} published sponsor records visible.`}</p>
          {canContent ? <Link to="/admin/sponsor-pages/master">Open sponsor CMS</Link> : <span className="admin-dashboard-note">Sponsor CMS hidden for this operator.</span>}
        </article>
        <article className="admin-card admin-dashboard-gate">
          <span className={statusClass(canIntegrations ? slackState : 'skipped')}>{canIntegrations ? slackState : 'no access'}</span>
          <h2>Slack / delivery</h2>
          <p>{canIntegrations ? `${deliveryCounts.pending} pending, ${sentSlackEvidence} sent with proof, ${deliveryCounts.failed + deliveryCounts.blocked} need attention.` : 'This operator lacks delivery-management rights.'}</p>
          {canIntegrations ? <Link to="/admin/integrations">Open deliveries</Link> : <span className="admin-dashboard-note">Delivery queue hidden for this operator.</span>}
        </article>
        <article className="admin-card admin-dashboard-gate">
          <span className={statusClass(migrationState)}>{migrationState}</span>
          <h2>Migration evidence</h2>
          <p>{expectedRuleTotal ? `${readyExpectedRuleCount}/${expectedRuleTotal} expected preapproval rules are persisted and usable.` : 'No expected preapproval rules configured for this dashboard.'}</p>
          {canMembers ? <Link to="/admin/members">Open domain rules</Link> : <span className="admin-dashboard-note">Domain rules hidden for this operator.</span>}
        </article>
      </section>

      <section className="admin-dashboard-section">
        <div>
          <span className="legal-eyebrow">Sponsor readiness</span>
          <h2>Current sponsor rooms</h2>
        </div>
        <div className="admin-dashboard-sponsors">
          {sponsors.map(item => {
            const expectedDomains = EXPECTED_DOMAIN_RULES[item.slug] ?? []
            const matchingRules = preapprovalRules.filter(rule => expectedDomains.includes(normalizeDomain(rule.email_domain)) && ruleSupportsSponsor(rule, item.slug))
            const grantCount = members.filter(member => (member.allowed_sponsors ?? []).includes(item.slug)).length
            const draftNewer = compareDates(item.cms?.draft?.created_at, item.cms?.published?.published_at)
            return (
              <article key={item.slug} className="admin-card admin-dashboard-sponsor">
                <div className="admin-member-main">
                  <div>
                    <h3>{item.label}</h3>
                    <p className="admin-muted">{item.description}</p>
                  </div>
                  <span className={statusClass(cmsState(item))}>{cmsState(item)}</span>
                </div>
                <dl className="admin-dashboard-facts">
                  <div><dt>CMS source</dt><dd>{item.cms?.source ?? item.cmsError ?? 'Not checked'}</dd></div>
                  <div><dt>Published</dt><dd>{item.cms?.published ? `v${item.cms.published.version} · ${formatDate(item.cms.published.published_at)}` : 'Not proven'}</dd></div>
                  <div><dt>Draft</dt><dd>{item.cms?.draft ? `${draftNewer ? 'Newer draft' : 'Draft'} · v${item.cms.draft.version}` : 'No draft visible'}</dd></div>
                  <div><dt>Member grants</dt><dd>{grantCount}</dd></div>
                  <div><dt>Domain rule</dt><dd>{expectedDomains.length ? `${matchingRules.length}/${expectedDomains.length} usable` : 'Not required'}</dd></div>
                  <div><dt>Protected route</dt><dd><span className={statusClass(routeState(item.protectedRoute))}>{item.protectedRoute?.error ?? (item.protectedRoute?.ok ? 'content' : 'not checked')}</span></dd></div>
                  <div><dt>Temp route</dt><dd>{item.slug === 'master' ? 'N/A' : <span className={statusClass(routeState(item.tempRoute))}>{item.tempRoute?.error ?? (item.tempRoute?.ok ? 'content' : 'not checked')}</span>}</dd></div>
                  <div><dt>Latest</dt><dd>{formatDate(latestSponsorTimestamp(item))}</dd></div>
                </dl>
                <div className="admin-member-actions">
                  {canContent && <Link to={`/admin/sponsor-pages/${item.slug}`} className="btn btn-secondary">CMS</Link>}
                  <Link to={`/sponsors/${item.slug}`} className="btn btn-secondary">Protected</Link>
                  {item.slug !== 'master' && <Link to={sponsorTempPath(item.slug)} className="btn btn-primary">Temp copy</Link>}
                </div>
              </article>
            )
          })}
        </div>
      </section>

      <section className="admin-dashboard-section">
        <div>
          <span className="legal-eyebrow">Production evidence</span>
          <h2>Blockers and proof gaps</h2>
        </div>
        {blockers.length === 0 ? (
          <div className="admin-card admin-dashboard-empty">
            <p>No hard blockers found from the current API checks. Live OTP and Slack event proof still need human-run evidence before production-ready status.</p>
          </div>
        ) : (
          <div className="admin-list">
            {blockers.map((blocker, index) => (
              <article key={`${blocker.title}-${index}`} className="admin-card admin-dashboard-blocker">
                <div>
                  <h3>{blocker.title}</h3>
                  <p className="admin-muted">{blocker.body}</p>
                </div>
                {blocker.href && <Link to={blocker.href} className="btn btn-secondary">Open</Link>}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
